import React from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Button, Dropdown, DropdownButton, Badge, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import FontAwesome from 'react-fontawesome';
import { makeObservable, observable, action } from 'mobx';
import { message } from 'antd';
import { isNumeric, isCategoricalLike, isBinary } from '../../UtilityClasses';
import ModifyCategorical from './ModifySingleVariable/ModifyCategorical';
import ModifyContinuous from './ModifySingleVariable/ModifyContinuous';
import ModifyBinary from './ModifySingleVariable/ModifyBinary';
import CombineModal from './CombineVariables/CombineModal';

/**
 * Component for displaying and modifying current variables in a table
 */
const VariableTable = inject(
	'variableManagerStore',
	'rootStore'
)(
	observer(
		class VariableTable extends React.Component {
			modifyCategoricalIsOpen = false;
			modifyContinuousIsOpen = false;
			modifyBinaryIsOpen = false;
			combineVariablesIsOpen = false;
			currentVariable = '';
			derivedVariable = '';
			combineVariables = [];
			callback = '';
			sortVarAsc = true;
			sortSourceAsc = true;
			sortTypeAsc = true;

			constructor() {
				super();
				makeObservable(this, {
					modifyCategoricalIsOpen: observable,
					modifyContinuousIsOpen: observable,
					modifyBinaryIsOpen: observable,
					combineVariablesIsOpen: observable,
					currentVariable: observable,
					derivedVariable: observable,
					combineVariables: observable,
					callback: observable,
					sortVarAsc: observable,
					sortSourceAsc: observable,
					sortTypeAsc: observable,
					closeModal: action,
					handleCogWheelClick: action,
					handleSort: action,
					combineSelected: action,
				});

				this.handleCogWheelClick = this.handleCogWheelClick.bind(this);
				this.closeModal = this.closeModal.bind(this);
				this.handleSort = this.handleSort.bind(this);
				this.combineSelected = this.combineSelected.bind(this);
			}

			/**
			 * creates the modal for modification
			 * @returns {(ModifyBinary|ModifyContinuous|ModifyBinary|CombineModal)}
			 */
			getModal() {
				let modal = null;
				if (this.modifyContinuousIsOpen) {
					modal = (
						<ModifyContinuous
							modalIsOpen={this.modifyContinuousIsOpen}
							variable={this.currentVariable}
							derivedVariable={this.derivedVariable}
							setColorRange={this.setColorRange}
							closeModal={this.closeModal}
						/>
					);
				} else if (this.modifyCategoricalIsOpen) {
					modal = (
						<ModifyCategorical
							modalIsOpen={this.modifyCategoricalIsOpen}
							variable={this.currentVariable}
							derivedVariable={this.derivedVariable}
							closeModal={this.closeModal}
						/>
					);
				} else if (this.modifyBinaryIsOpen) {
					modal = (
						<ModifyBinary
							modalIsOpen={this.modifyBinaryIsOpen}
							variable={this.currentVariable}
							derivedVariable={this.derivedVariable}
							closeModal={this.closeModal}
						/>
					);
				} else if (this.combineVariablesIsOpen) {
					modal = (
						<CombineModal
							modalIsOpen={this.combineVariablesIsOpen}
							variables={this.combineVariables}
							derivedVariable={this.derivedVariable}
							closeModal={this.closeModal}
						/>
					);
				}
				return modal;
			}

			/**
			 * closes all modals
			 */
			closeModal() {
				this.modifyCategoricalIsOpen = false;
				this.modifyContinuousIsOpen = false;
				this.modifyBinaryIsOpen = false;
				this.combineVariablesIsOpen = false;
			}

			/**
			 * opens modal to modify variable
			 * @param {OriginalVariable} originalVariable
			 * @param {DerivedVariable} derivedVariable
			 * @param {string} datatype
			 */
			openModifyModal(originalVariable, derivedVariable, datatype) {
				this.modifyContinuousIsOpen = isNumeric(datatype);
				this.modifyCategoricalIsOpen = isCategoricalLike(datatype);
				this.modifyBinaryIsOpen = isBinary(datatype);
				this.derivedVariable = derivedVariable;
				this.currentVariable = originalVariable;
			}

			/**
			 * opens modal to combine variables
			 * @param {OriginalVariable[]} variables
			 * @param {DerivedVariable} derivedVariable
			 */
			openCombineModal(variables, derivedVariable) {
				this.combineVariables = variables;
				this.derivedVariable = derivedVariable;
				this.combineVariablesIsOpen = true;
			}

			/**
			 * handles a cogwheelClick
			 * @param {event} event
			 * @param {string} id
			 */
			handleCogWheelClick(event, id) {
				const variable = this.props.variableManagerStore.getById(id);
				if (variable.originalIds.length === 1) {
					let originalVariable;
					let derivedVariable = null;
					if (variable.derived) {
						originalVariable = this.props.variableManagerStore.getById(variable.originalIds[0]);
						derivedVariable = variable;
					} else {
						originalVariable = variable;
					}
					this.openModifyModal(originalVariable, derivedVariable, originalVariable.datatype);
				} else {
					this.openCombineModal(
						variable.originalIds.map((d) => this.props.variableManagerStore.getById(d)),
						variable
					);
				}
			}

			/**
			 * removes a variable
			 * @param {(OriginalVariable|DerivedVariable)} variable
			 */
			removeVariable(variable) {
				if (variable.derived) {
					this.props.openSaveVarModal(variable, (save) => {
						this.props.variableManagerStore.updateSavedVariables(variable.id, save);
						this.props.variableManagerStore.removeVariable(variable.id);
					});
				} else {
					this.props.variableManagerStore.removeVariable(variable.id);
				}
			}

			/**
			 * displays the currently selected variables
			 * @returns {Table}
			 */
			showCurrentVariables() {
				const elements = [];
				this.props.variableManagerStore.currentVariables.forEach((d, i) => {
					const fullVariable = this.props.variableManagerStore.getById(d.id);
					const tooltip = <Tooltip id="tooltip">{fullVariable.description}</Tooltip>;
					let label = null;
					if (fullVariable.derived) {
						label = <Badge bg="info">Modified</Badge>;
					}
					let newLabel = null;
					if (d.isNew) {
						newLabel = <Badge bg="info">New</Badge>;
					}
					let bgColor = null;
					if (d.isSelected) {
						bgColor = '#cce5ff';
					}
					elements.push(
						<tr
							key={d.id}
							style={{ backgroundColor: bgColor }}
							onClick={(e) => {
								if (e.target.nodeName === 'TD') {
									this.props.variableManagerStore.toggleSelected(d.id);
								}
							}}
						>
							<td style={{ whiteSpace: 'nowrap' }}>
								<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
									<span style={{ minWidth: 20 }}>{i + 1}</span>
									<Button size="sm" onClick={() => this.moveSingle(true, false, i)}>
										<UpOutlined />
									</Button>
									<Button size="sm" onClick={() => this.moveSingle(false, false, i)}>
										<DownOutlined />
									</Button>
								</div>
							</td>
							<OverlayTrigger placement="top" overlay={tooltip}>
								<td>{fullVariable.name}</td>
							</OverlayTrigger>
							<td>
								{newLabel} {label}
							</td>
							<td>{fullVariable.datatype}</td>
							<td>
								{!fullVariable.derived
									? this.props.availableCategories.filter(
											(category) => category.id === fullVariable.profile
										)[0].name
									: 'Derived'}
							</td>
							<td>
								<FontAwesome onClick={(e) => this.handleCogWheelClick(e, d.id)} name="cog" />
								{'\t'}
								<FontAwesome
									onClick={() => {
										this.removeVariable(fullVariable);
									}}
									name="times"
								/>
							</td>
						</tr>
					);
				});
				return (
					<Table hover size="sm">
						<thead>
							<tr>
								<th style={{ whiteSpace: 'nowrap' }}>Position</th>
								<th style={{ whiteSpace: 'nowrap' }}>
									Variable
									{this.sortVarAsc ? (
										<DownOutlined
											onClick={() => this.handleSort('alphabet')}
											style={{ marginLeft: 8, cursor: 'pointer' }}
										/>
									) : (
										<UpOutlined
											onClick={() => this.handleSort('alphabet')}
											style={{ marginLeft: 8, cursor: 'pointer' }}
										/>
									)}
								</th>
								<th style={{ whiteSpace: 'nowrap' }} />
								<th style={{ whiteSpace: 'nowrap' }}>
									Datatype
									{this.sortTypeAsc ? (
										<DownOutlined
											onClick={() => this.handleSort('datatype')}
											style={{ marginLeft: 8, cursor: 'pointer' }}
										/>
									) : (
										<UpOutlined
											onClick={() => this.handleSort('datatype')}
											style={{ marginLeft: 8, cursor: 'pointer' }}
										/>
									)}
								</th>
								<th style={{ whiteSpace: 'nowrap' }}>
									Source
									{this.sortSourceAsc ? (
										<DownOutlined
											onClick={() => this.handleSort('source')}
											style={{ marginLeft: 8, cursor: 'pointer' }}
										/>
									) : (
										<UpOutlined
											onClick={() => this.handleSort('source')}
											style={{ marginLeft: 8, cursor: 'pointer' }}
										/>
									)}
								</th>
								<th style={{ whiteSpace: 'nowrap' }}>Actions</th>
							</tr>
						</thead>
						<tbody>{elements}</tbody>
					</Table>
				);
			}

			/**
			 * sorts current variables
			 * @param {string} category
			 */
			handleSort(category) {
				if (category === 'source') {
					this.props.variableManagerStore.sortBySource(
						this.props.availableCategories.map((d) => d.id),
						this.sortSourceAsc
					);
					this.sortSourceAsc = !this.sortSourceAsc;
				} else if (category === 'alphabet') {
					this.props.variableManagerStore.sortAlphabetically(this.sortVarAsc);
					this.sortVarAsc = !this.sortVarAsc;
				} else {
					this.props.variableManagerStore.sortByDatatype(this.sortTypeAsc);
					this.sortTypeAsc = !this.sortTypeAsc;
				}
			}

			/**
			 * Opens modal for combining selected variables
			 */
			combineSelected() {
				const selectedVar = this.props.variableManagerStore.getSelectedVariables();
				if (selectedVar.length > 1) {
					this.openCombineModal(this.props.variableManagerStore.getSelectedVariables(), null);
				} else {
					message.warning({
						content: 'Please select at least two features to combine',
						duration: 5,
					});
				}
			}

			/**
			 * moves selected variables
			 * @param {boolean} isUp
			 * @param {boolean} toExtreme
			 */
			moveSelected(isUp, toExtreme) {
				const indices = this.props.variableManagerStore.getSelectedIndices();
				this.props.variableManagerStore.move(isUp, toExtreme, indices);
			}

			/**
			 * moves single variable
			 * @param {boolean} isUp
			 * @param {boolean} toExtreme
			 * @param {number} index
			 */
			moveSingle(isUp, toExtreme, index) {
				this.props.variableManagerStore.move(isUp, toExtreme, [index]);
			}

			render() {
				return (
					<div>
						<div style={{ maxHeight: 400, overflowY: 'scroll', marginBottom: 15 }}>
							{this.showCurrentVariables()}
						</div>
						<div style={{ display: 'flex', gap: 10 }}>
							<DropdownButton title="Move Selected..." id="MoveSelected">
								<Dropdown.Item onClick={() => this.moveSelected(true, false)} eventKey="1">
									Up
								</Dropdown.Item>
								<Dropdown.Item onClick={() => this.moveSelected(false, false)} eventKey="2">
									Down
								</Dropdown.Item>
								<Dropdown.Divider />
								<Dropdown.Item onClick={() => this.moveSelected(true, true)} eventKey="3">
									to top
								</Dropdown.Item>
								<Dropdown.Item onClick={() => this.moveSelected(false, true)} eventKey="4">
									to bottom
								</Dropdown.Item>
							</DropdownButton>
							<Button onClick={this.combineSelected}>Combine Selected</Button>
						</div>
						{this.getModal()}
					</div>
				);
			}
		}
	)
);
VariableTable.propTypes = {
	availableCategories: PropTypes.arrayOf(PropTypes.object),
};
export default VariableTable;
